import { Flex, useMantineTheme } from '@mantine/core';
import debounce from 'debounce-promise';
import { when } from 'mobx';
import { observer } from 'mobx-react-lite';
import type { JSX } from 'react';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { Goal as GoalModel, TComparison } from '@feathr/blackbox';
import type { ISuggestion } from '@feathr/components';
import { FormElement, InputElement, SegmentedControl, Suggestions } from '@feathr/components';
import { useStore } from '@feathr/extender/state';
import { DEFAULT_DEBOUNCE_WAIT } from '@feathr/hooks';

import { buildURLVisitPredicates, getGoalSegmentName } from '../Goal.predicates';

import * as styles from './URLVisitGoal.css';

interface IProps {
  goal: GoalModel;
}

function URLVisitGoal({ goal }: Readonly<IProps>): JSX.Element {
  const { t } = useTranslation();
  const { Breadcrumbs, Campaigns, Segments } = useStore();

  const goalRef = useRef(goal);
  goalRef.current = goal;
  const [matchMode, setMatchMode] = useState<string>('eq');
  const [urlValue, setUrlValue] = useState<string>('');
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [suggestions, setSuggestions] = useState<ISuggestion[]>([]);
  const [isDropdownOpen, setIsDropdownOpen] = useState<boolean>(false);

  const {
    spacing: { '2': gap },
  } = useMantineTheme();

  const debouncedGetSuggestions = useMemo(
    () =>
      debounce(async (query: string): Promise<void> => {
        try {
          const path = 'breadcrumbs/loc_url/values';
          const results = await Breadcrumbs.getSuggestions(path, query);
          const mapped: ISuggestion[] = results
            .filter((item) => item.value)
            .map((item) => ({ value: item.value, label: item.value }));
          setSuggestions(mapped);
        } catch {
          setSuggestions([]);
        } finally {
          setIsLoading(false);
        }
      }, DEFAULT_DEBOUNCE_WAIT),
    [Breadcrumbs],
  );

  const debouncedUpdateSegment = useMemo(
    () =>
      debounce(async (url: string, mode: TComparison): Promise<void> => {
        if (!url) {
          return;
        }

        const predicates = buildURLVisitPredicates(url, mode);
        const segmentName = getGoalSegmentName(goalRef.current, Campaigns);

        const result = await Segments.getOrCreateGoalSegment(predicates, segmentName);
        goalRef.current.set({ segment: result.segment.id });
      }, DEFAULT_DEBOUNCE_WAIT),
    [Campaigns, Segments],
  );

  useEffect(() => {
    async function loadExistingSegment(): Promise<void> {
      const segmentId = goal.get('segment');
      if (!segmentId) {
        return;
      }
      const segment = Segments.get(segmentId);
      await when(() => segment.isFetched);
      const predicates = segment.get('predicates');

      // No predicates when a value hasn't been set for the first time
      if (!predicates?.length) {
        return;
      }

      // Handle both flat (current) and nested (legacy) predicate structures
      const firstPredicate = predicates[0];
      const urlPredicate =
        firstPredicate?.attr_against === 'loc_url'
          ? firstPredicate
          : firstPredicate?.group?.find((p) => p.attr_against === 'loc_url');
      if (urlPredicate?.value && typeof urlPredicate.value === 'string') {
        setUrlValue(urlPredicate.value);
        if (urlPredicate.comparison) {
          setMatchMode(urlPredicate.comparison);
        }
      }
    }

    loadExistingSegment();
  }, []);

  const data = [
    {
      label: t('Exact match'),
      value: 'eq',
    },
    {
      label: t('Starts with'),
      value: 'starts_with',
    },
  ];

  function handleInputChange(value: string = ''): void {
    setUrlValue(value);
    setIsLoading(true);
    debouncedGetSuggestions(value);
    debouncedUpdateSegment(value, matchMode as TComparison);
  }

  function handleInputFocus(): void {
    setIsDropdownOpen(true);
    setIsLoading(true);
    debouncedGetSuggestions(urlValue);
  }

  function handleInputBlur(): void {
    setIsDropdownOpen(false);
  }

  function handleSuggestionClick(value: string): void {
    setUrlValue(value);
    setIsDropdownOpen(false);
    debouncedUpdateSegment(value, matchMode as TComparison);
  }

  function handleChangeMatchMode(value: string): void {
    setMatchMode(value);
    if (urlValue) {
      debouncedUpdateSegment(urlValue, value as TComparison);
    }
  }

  return (
    <Flex direction={'column'} gap={gap}>
      <Flex align={'flex-end'} gap={gap}>
        <FormElement label={t('Target URL')}>
          <SegmentedControl data={data} onChange={handleChangeMatchMode} value={matchMode} />
        </FormElement>
        <div className={styles.inputWrapper} onBlur={handleInputBlur} onFocus={handleInputFocus}>
          <InputElement
            aria-label={t('URL input')}
            onChange={handleInputChange}
            placeholder={t('Start typing to search URLs')}
            value={urlValue}
            wrapperClassName={styles.input}
          />
          {isDropdownOpen && (
            <div className={styles.dropdown}>
              <Suggestions
                isLoading={isLoading}
                onClickSuggestion={handleSuggestionClick}
                suggestions={suggestions}
              />
            </div>
          )}
        </div>
      </Flex>
    </Flex>
  );
}

export default observer(URLVisitGoal);
